import { useIsMobile } from 'Hooks';
import { TFunction } from 'i18next';
import { Dropdown, Button, Menu } from 'antd';
import { Icon, DateFormatted } from 'Components';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import { SystemRole } from 'Services/Api/Roles/Types';
import { getUserRoleLabel } from 'app-util';
import { usePermission } from 'Components/Permission/Permission.hooks';
import { PermissionCode } from 'Services/Api/Users/Types';
import { useTranslation } from 'react-i18next';
import { RoleEllipsisText, RoleRowActions } from '../columns';
import { APP_CONFIG } from 'app-config';
import moment from 'moment';

type RolesMobileViewProps<T extends SystemRole> = {
    records: T[];
    totalCount: number;
    selectedTenant: number;
    currentPage: number;
    setCurrentPage: () => void;
    t: TFunction;
    actions?: RoleRowActions<T>;
};

const RolesMobileView = <T extends SystemRole>({
    records,
    totalCount,
    currentPage,
    selectedTenant,
    setCurrentPage,
    t,
    actions
}: RolesMobileViewProps<T>) => {
    return (
        <div className="records-section roles-records-section">
            <InfinityScrollWrapper
                dataLength={records.length}
                hasMore={currentPage < Math.ceil(totalCount / 10)}
                onLoadMore={() => {
                    setTimeout(() => {
                        setCurrentPage();
                    }, 500);
                }}
                height={600}
                endMessageText={t(
                    'phishingManager.campaignSetup.step3.property.allItemLoaded'
                )}
                scrollableId="roles-scroll-container"
            >
                {records.map((record) => (
                    <div key={record.roleName} className="record">
                        <div className="row">
                            <span className="title">
                                <RoleEllipsisText text={getUserRoleLabel(record.roleName)} />
                            </span>
                            <RoleMobileActionsButton selectedTenant={selectedTenant} record={record} actions={actions} />
                        </div>
                        <div className="row">
                            <span className="label">
                                {t('roles.columns.description')}
                            </span>
                            <span className="title">
                                <RoleEllipsisText text={record.description} fallback="-" />
                            </span>
                        </div>
                        <div className="row">
                            <span className="label">{t('roles.columns.type')}</span>
                            <span
                                className={
                                    record.type === 'Primary'
                                        ? 'primary-role-batch'
                                        : 'secondary-role-batch'
                                }
                            >
                                {record.type}
                            </span>
                        </div>
                        <div className="row">
                            <span className="label">
                                {t('roles.columns.usersCount')}
                            </span>
                            <span className="title">{record.usersCount ?? 0}</span>
                        </div>
                        <div className="row">
                            <span className="label">
                                {t('roles.columns.lastUpdate')}
                            </span>
                            <span className="title">
                                {!!record.updatedDate ? moment(record.updatedDate).format(APP_CONFIG.MONTH_DATE_FORMAT) : ""}
                            </span>
                        </div>
                    </div>
                ))}
            </InfinityScrollWrapper>
        </div>
    );
};

type RoleMobileActionsButtonProps<T extends SystemRole> = {
    record: T;
    selectedTenant: number;
    actions?: RoleRowActions<T>;
};

function RoleMobileActionsButton<T extends SystemRole>({
    record,
    selectedTenant,
    actions
}: RoleMobileActionsButtonProps<T>) {
    const { t } = useTranslation();
    const { getHasPermission } = usePermission();

    const canView = !!getHasPermission({ code: PermissionCode.ViewRoles }).permitted;
	const canEdit = record.type?.toLowerCase() === 'primary' ? !!getHasPermission({ code: PermissionCode.EditSystemRole }).permitted: !!getHasPermission({ code: PermissionCode.EDIT_SECONDARY_ROLE }).permitted;
    const canRestore = !!getHasPermission({ code: PermissionCode.RESTORE_PRIMARY_ROLE }).permitted;
    const canClone = !!selectedTenant && !!getHasPermission({ code: PermissionCode.CREATE_SECONDARY_ROLE }).permitted;
    const canDelete = !!getHasPermission({ code: PermissionCode.DELETE_SECONDARY_ROLE }).permitted;

    const showRestore =
        canRestore &&
        record.type?.toLowerCase() === 'primary' &&
        !!record?.isPrimaryRoleOverride;
    const showDelete = canDelete && record.type !== 'Primary';

    const menu = (
        <Menu className="or-list-selector">
            {canView && (
                <Menu.Item
                    key="viewDetail"
                    onClick={() => actions?.onViewDetail?.(record)}
                >
                    {t('roles.actions.view')}
                </Menu.Item>
            )}
            {canEdit && (
                <Menu.Item key="edit" onClick={() => actions?.onEdit?.(record)}>
                    {t('roles.actions.edit')}
                </Menu.Item>
            )}
            {showRestore && (
                <Menu.Item
                    key="restoreToDefaults"
                    onClick={() => actions?.onRestoreToDefaults?.(record)}
                >
                    {t('roles.actions.restoreToDefaults')}
                </Menu.Item>
            )}
            {canClone && (
                <Menu.Item key="clone" onClick={() => actions?.onClone?.(record)}>
                    {t('roles.actions.clone')}
                </Menu.Item>
            )}
            {showDelete && (
                <Menu.Item key="delete" onClick={() => actions?.onDelete?.(record)}>
                    <span className="role-action-delete">
                        {t('roles.actions.delete')}
                    </span>
                </Menu.Item>
            )}
        </Menu>
    );

    return (
        <Dropdown
            className="role-action-button"
            overlay={menu}
            arrow={false}
            trigger={['click']}
            placement="bottomRight"
        >
            <Button
                type="text"
                icon={<Icon type="optionIcon" />}
                onClick={(e) => e.stopPropagation()}
            />
        </Dropdown>
    );
}

export default RolesMobileView;